/*
 * CAPTURE-OWNED MODULE
 * Edge auto-scroll for drags in the template zone viewer.
 *
 * TemplateZoneViewer owns the loop and provides DragScrollContext; a gesture
 * in a page overlay calls beginDragScroll(press) when it starts and
 * endDragScroll() when it ends. With no provider both are no-ops.
 *
 * While the pointer is within EDGE_ZONE_PX of an edge of the scroll container
 * (or past it), the container scrolls toward that edge, faster the deeper the
 * pointer is: a quadratic ramp up to MAX_SPEED_PX per 60 Hz frame, or a fixed
 * REDUCED_SPEED_PX under prefers-reduced-motion. Nothing scrolls until the
 * pointer has moved more than PRESS_THRESHOLD_PX from the press.
 *
 * After every frame that actually scrolled, one synthetic mousemove carrying
 * the last pointer position is dispatched on window (marked AUTOSCROLL_MOVE),
 * so the gesture recomputes against the moved layout though the pointer is
 * still.
 */
import { createContext } from 'react';

export const EDGE_ZONE_PX = 40;
export const MAX_SPEED_PX = 20;
export const REDUCED_SPEED_PX = 4;
export const PRESS_THRESHOLD_PX = 4;
export const FRAME_MS = 1000 / 60;
// A long frame (tab in the background, a stall) scrolls at most this many frames' worth.
export const MAX_FRAME_FACTOR = 3;

// Marks the synthetic mousemove the loop dispatches.
export const AUTOSCROLL_MOVE = '__tzvAutoScrollMove';

/**
 * Signed scroll speed in px per 60 Hz frame for a pointer at `pos` on an axis
 * whose visible extent is [lo, hi]: negative toward lo, positive toward hi,
 * 0 outside both edge zones.
 */
export function edgeSpeed(pos, lo, hi, reduced = false) {
  let depth;
  let dir;
  if (pos < lo + EDGE_ZONE_PX) {
    depth = lo + EDGE_ZONE_PX - pos;
    dir = -1;
  } else if (pos > hi - EDGE_ZONE_PX) {
    depth = pos - (hi - EDGE_ZONE_PX);
    dir = 1;
  } else {
    return 0;
  }
  if (reduced) return dir * REDUCED_SPEED_PX;
  const t = Math.min(1, depth / EDGE_ZONE_PX);
  return dir * Math.max(1, MAX_SPEED_PX * t * t);
}

/** Whole pixels to scroll this frame for `speed` and a frame of `dt` ms. */
export function frameStep(speed, dt) {
  if (!speed) return 0;
  const f = Math.min(MAX_FRAME_FACTOR, Math.max(0, dt) / FRAME_MS);
  return Math.sign(speed) * Math.max(1, Math.round(Math.abs(speed) * f));
}

export function prefersReducedMotion() {
  try {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  } catch {
    return false;
  }
}

const noop = () => {};
export const DragScrollContext = createContext({ beginDragScroll: noop, endDragScroll: noop });
